import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";

import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import BaseButton from "@reusables/BaseButton";
import { TabProperties } from "../../types";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { CustomerFormTyping } from "./TabsController";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import { ReactComponent as PlusSVG } from "@assets/icons/ic_plus.svg";
import BaseCheckbox from "@reusables/BaseCheckbox";
import { Stack } from "@mui/material";
import FormErrorWarning from "../modals/FormErrorWarning";
import { Can } from "@/casl.config";

export default function GeneralTab({onNext, mode = "creation"}: TabProperties) {
    const { t } = useTranslation("", { keyPrefix: "customers.general.tabs.general" });

    const {control, setValue, getValues} = useFormContext<CustomerFormTyping>();

    const {fields: contacts, append: appendContact, remove} = useFieldArray({
        control,
        name: "contacts",
        keyName: "loop_id"
    });

    const {fields: personContacts, append: appendPersonContact} = useFieldArray({
        control,
        name: "person_contacts",
        keyName: "loop_id"
    });

    const isPerson = useWatch({
        control,
        name: "is_person",
        defaultValue: false
    });

    useEffect(() => {
        if (isPerson) {
            if (personContacts.length === 0) {
                appendPersonContact({});
            }
            return;
        }

        if (contacts.length === 0) {
            appendContact({});
        }
    }, [appendContact, appendPersonContact, contacts.length, isPerson, personContacts.length, getValues]);

    return (
        <form className="space-y-[32px]" onSubmit={onNext}>
            <FormErrorWarning />

            <Stack
                direction={"row"}
                spacing={2}
            >
                <Controller
                    control={control}
                    name="is_person"
                    render={({field}) => (
                        <BaseCheckbox
                            value={!field.value}
                            onChange={val => {
                                if(val)
                                    field.onChange(!val);
                            }}

                            disabled={mode == "editing"}

                            label={t("checkboxes.isCompany")}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="is_person"
                    render={({field}) => (
                        <BaseCheckbox
                            value={field.value}
                            onChange={val => {
                                if(val)
                                    field.onChange(val);
                            }}

                            disabled={mode == "editing"}

                            label={t("checkboxes.isPerson")}
                        />
                    )}
                />
            </Stack>

            <BaseInputsGrid>

                <Controller
                    control={control}
                    name="name"
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            label={t("fields.customerName.label") + " *"}
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="code"
                    render={({field, fieldState}) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}

                            label={t("fields.customerCode.label")}

                            disabled={mode == "editing"}
                        />
                    )}
                />

                {!isPerson && (
                    <Controller
                        control={control}
                        name="registration_number"
                        render={({field, fieldState}) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}

                                label={t("fields.registrationNumber.label")}
                            />
                        )}
                    />
                )}
            </BaseInputsGrid>

            <Can I="contact_detail.view" a="customer">
                {
                    isPerson ? (
                        <BaseInputsGrid key={personContacts[0]?.loop_id ?? "person-contact"}>
                            <Controller
                                    control={control}
                                    name={`person_contacts.0.phone`}
                                    render={({field, fieldState}) => (
                                        <BasePhoneInput
                                            {...field}
                                            {...fieldState}
                                            label={t('fields.phone.label')}
                                            size='md'
                                            variant='custom'
                                        />
                                    )}
                                />

                                <Controller
                                    control={control}
                                    name={`person_contacts.0.email`}
                                    render={({field, fieldState}) => (
                                        <BaseInput
                                            {...field}
                                            error={fieldState.error}

                                            label={t("fields.email.label")}
                                            type={"email"}
                                        />
                                    )}
                                />
                        </BaseInputsGrid>
                    ) : (
                        <div>
                            <div className="flex items-center justify-between">
                                <div className="text-xl text-accent">{t("categories.contactInfo")}</div>
                                <BaseMaterialIconButton
                                    icon={<PlusSVG />}
                                    onClick={() => appendContact({})}
                                />
                            </div>

                            {contacts.map((contact, index) => {
                                const isExtraContact = index > 0;
                                const isLastContact = index === contacts.length - 1;

                                return (
                                    <div
                                        key={contact.loop_id}
                                        style={getReferenceItemStyle(isLastContact)}
                                    >
                                        <div className="grid grid-cols-1 gap-y-[24px] lg:grid-cols-3 lg:gap-x-[40px]">
                                            <div className="min-w-0">
                                                <Controller
                                                    control={control}
                                                    name={`contacts.${index}.name`}
                                                    render={({ field, fieldState }) => (
                                                        <BaseInput
                                                            {...field}
                                                            error={fieldState.error}
                                                            placeholder={t("fields.contactName.label")}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <div className="min-w-0">
                                                <Controller
                                                    control={control}
                                                    name={`contacts.${index}.phone`}
                                                    render={({ field, fieldState }) => (
                                                        <BasePhoneInput
                                                            {...field}
                                                            {...fieldState}
                                                            placeholder={t('fields.phone.label')}
                                                            size='md'
                                                            variant='custom'
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <div className="min-w-0">
                                                <Controller
                                                    control={control}
                                                    name={`contacts.${index}.email`}
                                                    render={({ field, fieldState }) => (
                                                        <BaseInput
                                                            {...field}
                                                            error={fieldState.error}
                                                            placeholder={t("fields.email.label")}
                                                            type={"email"}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {isExtraContact && (
                                            <div className="flex justify-end mt-[24px]">
                                                <div
                                                    className="text-gray-300 space-x-[10px] font-thin cursor-pointer hover:text-actionableIcons"
                                                    onClick={() => {
                                                        if (contact.id != undefined) {
                                                            setValue("deleted_contacts", [...getValues("deleted_contacts"), contact.id]);
                                                        }

                                                        remove(index);
                                                    }}
                                                >
                                                    <TrashSVG />
                                                    <span className="align-middle">{t("buttons.delete")}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )
                }
            </Can>

            <div>
                <div className="text-xl text-accent mb-[16px]">{t("categories.roles")}</div>
                <Stack
                    direction={"row"}
                    spacing={2}
                >
                    <BaseCheckbox
                        value
                        disabled
                        label={t("checkboxes.isCustomer")}
                    />

                    <Controller
                        control={control}
                        name="is_supplier"
                        render={({field}) => (
                            <BaseCheckbox
                                value={field.value}
                                onChange={field.onChange}
                                label={t("checkboxes.isSupplier")}
                            />
                        )}
                    />
                </Stack>
            </div>

            <div className="flex justify-center">
                <BaseButton
                    text={t("buttons.next")}
                    size="md"
                    buttonWidth="200px"
                />
            </div>
        </form>
    );
}

const getReferenceItemStyle = (isLastItem: boolean): React.CSSProperties => ({
    padding: "24px 0",
    ...(isLastItem ? {} : { borderBottom: "1px solid #D9D6DA" }),
});
